import { fromUnixTime, getUnixTime } from "date-fns";
import { useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import {
  GetBookingQuery,
  useBrandLocationsQuery,
} from "../../../graphql/generated";
import useGqlClient from "../../../hooks/useGqlClient";
import { StyledDateInput } from "../../../routes/brands/Listing/availability/Event";
import { authSelectors } from "../../../store/auth/selector";
import { lightTheme } from "../../../styles/theme";
import { Button, ButtonWrap } from "../../CTA";
import { SingleCheckBox } from "../../Checkbox";
import { DatePicker } from "../../DatePicker";
import { Dropdown } from "../../Dropdown";
import { Flex } from "../../Flex";
import Loader from "../../Loader";
import { Underline } from "../../Tabs";
import { Text } from "../../Text";
import { View } from "../../View";
import { BookingsIcon } from "../../icons/BookingsIcon";

const StyledFlex = styled(Flex)`
  display: flex;
  align-items: stretch;
  flex-direction: column;
`;

interface Props {
  onConfirm: (newDateTime?: number, newLocationId?: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  booking: GetBookingQuery["booking"];
  creatorName: string;
}

export const UpdateBookingOverlay = (props: Props) => {
  const client = useGqlClient();
  const activeBrandId = useSelector(authSelectors.activeBrandId);
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const { data: locationsData } = useBrandLocationsQuery(client, {
    brandId: activeBrandId ?? "",
  });

  let brandLocations = locationsData?.brand?.locations.locations.map((l) => ({
    label: l.name,
    value: l.id,
  }));
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(
    props.booking.confirmedTimeslot?.date
      ? fromUnixTime(props.booking.confirmedTimeslot.date)
      : null
  );
  const [selectedLocation, setSelectedLocation] = useState<string | null>(
    props.booking.location?.id ?? null
  );

  const originalDateUnix = props.booking.confirmedTimeslot?.date ?? null;
  const selectedDateUnix = selectedDateTime
    ? getUnixTime(selectedDateTime)
    : null;
  const isDateChanged = selectedDateUnix !== originalDateUnix;
  const isLocationChanged = selectedLocation !== props.booking.location?.id;
  const isChanged = isDateChanged || isLocationChanged;

  const handleSubmit = () => {
    if (!isChanged) {
      props.onCancel();
      return;
    }
    props.onConfirm(
      isDateChanged ? (selectedDateUnix as number) : undefined,
      isLocationChanged ? selectedLocation ?? undefined : undefined
    );
  };

  return (
    <>
      {props.isLoading ? (
        <Flex align="center" justify="center">
          <Loader />
        </Flex>
      ) : (
        <StyledFlex>
          <Text
            weight="bold"
            size="xl"
            margin={"0 0 0 0"}
            colorPreset="heading"
          >
            Update Booking
          </Text>
          <Underline selected />
          <Text margin={"m 0 0 0"} colorPreset="secondary" isInline>
            Please confirm with {props.creatorName} before making changes to
            this booking
          </Text>

          <Text weight="bold" margin="l 0 xs">
            Date & Time
          </Text>

          <DatePicker
            showTime={true}
            dateFormat="EEE, do MMMM 'at' h:mm a"
            placeholderText="Select new date and time"
            isClearable={false}
            startDate={new Date()}
            date={selectedDateTime}
            setDate={setSelectedDateTime}
            icon={
              <div
                style={{
                  margin: "auto 0",
                }}
              >
                <div
                  style={{
                    marginTop: 3,
                    marginRight: lightTheme.spacing.xl,
                    marginLeft: lightTheme.spacing.s,
                  }}
                >
                  <BookingsIcon colorPreset="secondary" width={17} />
                </div>
              </div>
            }
            customInput={
              <StyledDateInput
                clearable={false}
                noDates={false}
                hasIcon={true}
              />
            }
          />

          <Text weight="bold" margin="l 0 xs">
            Location
          </Text>
          <Dropdown
            width={420}
            showSelectAll
            disableSelection={brandLocations && brandLocations.length === 0}
            selectionMode="single"
            options={brandLocations ?? []}
            selectedOptions={selectedLocation ? [selectedLocation] : []}
            setSelectedOptions={(value) => {
              setSelectedLocation(value[0]);
            }}
            renderLabel={() => {
              if (!brandLocations || !selectedLocation) {
                return "Select Locations";
              }

              const firstLocation = brandLocations
                .filter((l) => selectedLocation === l.value)
                .sort((a, b) => a.label.localeCompare(b.label))[0];

              return firstLocation ? firstLocation.label : "";
            }}
            placeholder="Select Locations"
          />

          {isChanged ? (
            <Flex
              margin="xl 0 xs"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setHasConfirmed(!hasConfirmed);
              }}
            >
              <View margin="xxs 0 0">
                <SingleCheckBox checked={hasConfirmed} size="s" />
              </View>
              <Text margin="0 0 0 s" style={{ userSelect: "none" }}>
                The creator has confirmed they’re happy with this change
              </Text>
            </Flex>
          ) : null}

          <ButtonWrap margin="xl 0 0">
            <Button buttonType="secondary" onClick={props.onCancel}>
              Cancel
            </Button>
            <Button
              disabled={!hasConfirmed}
              isDisabled={!hasConfirmed}
              onClick={handleSubmit}
              title="Update Booking"
            >
              {props.isLoading ? "Loading..." : "Update Booking"}
            </Button>
          </ButtonWrap>
        </StyledFlex>
      )}
    </>
  );
};
